import React from 'react';
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

const QuickStatsPanel = ({ timelineData, filter = { category: 'Global' } }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const embodiedNode = timelineData.find(d => d.title === 'Embodied Carbon');
    const operationalNode = timelineData.find(d => d.title === 'Operational Carbon');

    const isOperationalScope = filter.category?.toLowerCase() === "whole_building";
    const isGlobal = filter.category === "Global";

    // For Carbon Split
    let splitA = { label: 'Embodied', value: embodiedNode?.value || 0, color: embodiedNode?.color, pct: 0 };
    let splitB = { label: 'Operational', value: operationalNode?.value || 0, color: operationalNode?.color, pct: 0 };

    if (isOperationalScope) {
        // Show B6 vs B7
        const b6Val = operationalNode?.subNodes?.find(s => s.id.startsWith('B6'))?.value || 0;
        const b7Val = operationalNode?.subNodes?.find(s => s.id.startsWith('B7'))?.value || 0;
        splitA = { label: 'Operational Energy (B6)', value: b6Val, color: '#f59e0b', pct: 0 };
        splitB = { label: 'Operational Water (B7)', value: b7Val, color: '#0ea5e9', pct: 0 };
    }

    const totalSplit = splitA.value + splitB.value;
    splitA.pct = totalSplit > 0 ? Math.round((splitA.value / totalSplit) * 100) : 0;
    splitB.pct = totalSplit > 0 ? Math.round((splitB.value / totalSplit) * 100) : 0;

    const totalStages = isOperationalScope
        ? [splitA, splitB].filter(s => s.value > 0).length
        : timelineData.filter(d => !d.isCenter && Math.round(d.value) !== 0).length;

    const highestImpact = isOperationalScope
        ? [splitA, splitB].reduce((max, s) => s.value > max.value ? s : max, { value: 0, label: 'N/A', color: '#999' })
        : timelineData.reduce((max, node) => !node.isCenter && node.value > max.value ? { value: node.value, label: node.title, color: node.color } : max, { value: 0, label: 'N/A', color: '#999' });

    return (
        <div style={{
            background: isDark ? '#27272a' : 'white',
            padding: '12px 14px',
            borderRadius: '12px',
            boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.08)',
            border: `1px solid ${isDark ? '#3f3f46' : '#f1f5f9'}`,
            width: '100%',
            marginBottom: '12px'
        }}>
            <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t("Quick Stats")}</div>

            <div style={{ marginBottom: '8px' }}>
                <div style={{ fontSize: '10px', color: isDark ? '#94a3b8' : '#94a3b8', marginBottom: '1px' }}>{isOperationalScope ? t('Operational Stages') : t('Total Stages')}</div>
                <div style={{ fontSize: '15px', fontWeight: 700, color: isDark ? '#f1f5f9' : '#334155' }}>{totalStages}</div>
            </div>

            <div style={{ marginBottom: '10px' }}>
                <div style={{ fontSize: '10px', color: '#94a3b8', marginBottom: '1px' }}>{t("Highest Impact")}</div>
                <div style={{ fontSize: '12px', fontWeight: 600, color: highestImpact.color }}>
                    {t(highestImpact.label)}
                </div>
            </div>

            {(isGlobal || isOperationalScope) && (
                <div style={{ borderTop: isDark ? '1px solid #3f3f46' : '1px solid #f1f5f9', paddingTop: '10px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: isDark ? '#94a3b8' : '#64748b', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{t("Carbon Split")}</div>
                    <div style={{ marginBottom: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                            <span style={{ fontSize: '10px', color: '#94a3b8' }}>{t(splitA.label)}</span>
                            <span style={{ fontSize: '10px', fontWeight: 700, color: splitA.color }}>{splitA.pct}%</span>
                        </div>
                        <div style={{ width: '100%', height: '5px', background: isDark ? '#3f3f46' : '#f1f5f9', borderRadius: '3px', overflow: 'hidden' }}>
                            <div style={{ width: `${splitA.pct}%`, height: '100%', background: splitA.color, transition: 'width 0.3s ease' }}></div>
                        </div>
                    </div>
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                            <span style={{ fontSize: '10px', color: '#94a3b8' }}>{t(splitB.label)}</span>
                            <span style={{ fontSize: '10px', fontWeight: 700, color: splitB.color }}>{splitB.pct}%</span>
                        </div>
                        <div style={{ width: '100%', height: '5px', background: isDark ? '#3f3f46' : '#f1f5f9', borderRadius: '3px', overflow: 'hidden' }}>
                            <div style={{ width: `${splitB.pct}%`, height: '100%', background: splitB.color, transition: 'width 0.3s ease' }}></div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuickStatsPanel;
